import { createSelector } from "reselect"
import { get, chain, sumBy, every, some, findLast } from "lodash"
import moment from "moment"
import { getSelectedDate, getEndDate } from "selectors/dates"
import { getCategories } from "./categories"
import { FACTORS_THRESHOLD } from "helpers/analyzeConstants"
import { getDatesAdjusted as getDatesAdjustedHelper } from "helpers/dates"
import { getPeriod } from "modules/portfolio-root/selectors/portfolios"
import { getLatestDate } from "modules/portfolio-root/selectors/dates"

const getRisk = state => get(state, "global.data.overviewRisk.data.model.simulation.risk")
const getGmvData = state => get(state, "global.data.overviewRisk.data.model.simulation.composition")

export const getRiskStartDate = createSelector(
  [getRisk],
  risk => chain(risk)
    .first()
    .get("date")
    .value(),
)

const getRiskByCategory = createSelector(
  [getRisk],
  (risk) => chain(risk)
    .map((datum, index) => ({
      date: get(datum, "date"),
      index,
      categories: chain(datum)
        .get("attribution.factors")
        .groupBy(item => item.category)
        .mapValues(factors => sumBy(factors, factor => factor.value))
        .set("specific", get(datum, "attribution.summary.specific"))
        .value(),
    }))
    .value(),
)

export const getRiskTrends = createSelector(
  [getCategories, getRiskByCategory],
  (categories, riskByCategory) =>
    categories
      .map(category => ({
        data: chain(riskByCategory)
          .map((item, index) => ({
            date: moment(item.date).toDate(),
            value: category.fn
              ? category.fn(item.categories)
              : get(item.categories, category.id, 0),
            index,
          }))
          .value(),
        ...category,
      }))
      .filter(category => category.id === "specific" || !every(category.data, datum => Math.abs(datum.value) < FACTORS_THRESHOLD)),
)

export const getIdioVsFactors = createSelector(
  [getRiskTrends],
  (riskTrends) => chain(riskTrends)
    .last()
    .get("data")
    .filter("value")
    .value(),
)

export const getGmv = createSelector(
  [getGmvData, getRiskStartDate],
  (gmvData, firstDate) =>
    chain(gmvData)
      .filter(data => data.date >= firstDate)
      .map(({ date, gmv, equity }, index) => ({
        date: moment(date).toDate(),
        value: equity !== null ? equity : gmv,
        index,
      }))
      .value(),
)

export const getGmvTitle = createSelector(
  [getGmvData],
  gmvData => some(gmvData, item => item.equity !== null) ? "Assets" : "GMV",
)

export const getDailyRisk = createSelector(
  [getSelectedDate, getRisk],
  (selectedDate, risk) => findLast(risk, date => date.date <= selectedDate),
)

export const getDatesAdjusted = createSelector(
  [getPeriod, getRiskStartDate, getEndDate, getLatestDate],
  getDatesAdjustedHelper,
)
